import AtgGifLoader from '@common/AtgGifLoader';
import { Box } from '@atgfr8/atg-ui-tailwind';
import { Button } from '@components';
import { StickyPillTabs } from '@mobileComponents/StickyPillTabs';
import {
  customerDetailsStore,
  updateCustomerDetails,
} from '@mobileStore/customer';
import {
  AccountCircleOutlineIcon,
  HelpCircleOutlineIcon,
  FormatListBulletedIcon,
  CurrencyUsdIcon,
  ContentSaveIcon,
} from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { createSignal, Match, Show, Switch } from 'solid-js';
import { dashboardStore } from '@mobileStore/global/store';
import { getAlignmentClassMobile } from '@mobileUtils/helper';

import { CustomerRequestCredit } from './CustomerRequestCredit';
import CustomerDetails from './Details';
import Financials from './Financials';
import OrderHistory from './OrderHistory';
import RequestHistory from './RequestHistory';

type CustomerTab = 'details' | 'financials' | 'orderHistory' | 'requests';

const tabs = [
  { id: 'details', label: 'Details', icon: AccountCircleOutlineIcon },
  { id: 'financials', label: 'Financials', icon: CurrencyUsdIcon },
  { id: 'orderHistory', label: 'Order History', icon: FormatListBulletedIcon },
  { id: 'requests', label: 'Requests', icon: HelpCircleOutlineIcon },
] as const;

const CustomerInfo = () => {
  const [tab, setTab] = createSignal<CustomerTab>('details');
  const [saveLoading, setSaveLoading] = createSignal(false);
  const [isCreditRequestSheetOpen, setIsCreditRequestSheetOpen] =
    createSignal(false);
  const handleCustomerSave = async () => {
    try {
      setSaveLoading(true);
      const updatedContact = customerDetailsStore.customer.contacts.map(
        (contact) => {
          return {
            ...contact,
            id: contact.operationType === 'Insert' ? 0 : contact.id,
          };
        },
      );
      const customerPayload = {
        ...customerDetailsStore.customer,
        contacts: updatedContact,
        operationType: 'Update',
      };
      await updateCustomerDetails(customerPayload);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Box class="bg-[#e1ecf2] flex flex-col">
      <Show when={saveLoading()}>
        <AtgGifLoader />
      </Show>
      <Box
        style={{
          position: 'sticky',
          bottom: 0,
          'background-color': '#fff',
          'border-top': '1px solid #dcdcdc',
          'border-bottom-left-radius': '16px',
          'border-bottom-right-radius': '16px',
          'z-index': 10,
        }}
        class={getAlignmentClassMobile(
          dashboardStore.isSearchGlobalOpen,
          dashboardStore.globalSearchHasError,
        )}
      >
        <StickyPillTabs
          tabs={tabs}
          value={tab}
          onChange={setTab}
          actionButton={
            <Show when={tab() === 'details'}>
              <div class="flex flex-col gap-2">
                <Button
                  label="Save Customer"
                  variant="contained"
                  size="md"
                  onClick={() => handleCustomerSave()}
                  class="w-full text-[16px]!"
                  dataTestId={formatDataTestId('save-customer', 'button')}
                  isLoading={saveLoading()}
                  disabled={customerDetailsStore.isLoading || saveLoading()}
                  startIcon={<ContentSaveIcon size={20} />}
                />
                <Button
                  label="Request More Credit"
                  variant="contained"
                  size="md"
                  onClick={() => setIsCreditRequestSheetOpen(true)}
                  class="w-full text-[16px]!"
                  dataTestId={formatDataTestId('request-more-credit', 'button')}
                  startIcon={<CurrencyUsdIcon size={20} />}
                  disabled={
                    customerDetailsStore.isLoading ||
                    customerDetailsStore.customer.id == null
                  }
                />
              </div>
            </Show>
          }
        />
      </Box>
      <Show when={customerDetailsStore.customer.id != null}>
        <CustomerRequestCredit
          isOpen={isCreditRequestSheetOpen()}
          customerId={customerDetailsStore.customer.id as number}
          onClose={() => setIsCreditRequestSheetOpen(false)}
          onSubmitSuccess={() => setIsCreditRequestSheetOpen(false)}
        />
      </Show>
      <Box class="mt-[56px] overflow-auto">
        <Switch>
          <Match when={tab() === 'details'}>
            <CustomerDetails />
          </Match>
          <Match when={tab() === 'financials'}>
            <Financials />
          </Match>
          <Match when={tab() === 'orderHistory'}>
            <OrderHistory />
          </Match>
          <Match when={tab() === 'requests'}>
            <RequestHistory />
          </Match>
        </Switch>
      </Box>
    </Box>
  );
};

export default CustomerInfo;
